import { Injectable } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Firestore, collection, collectionData, doc, limit, orderBy, query, setDoc } from '@angular/fire/firestore';
import { traceUntilFirst } from '@angular/fire/performance';
import { take } from 'rxjs';

import { AuthenticatedDataService } from 'src/app/shared/services/authenticated-data.service';
import { ErrorReportingService, getErrorCode } from './error-reporting.service';
import { UserIdService } from './user-id.service';
import { UserRolesService } from './user-roles.service';
import { UserHistoryModel } from '../models/user-history.model';

@Injectable({
  providedIn: 'root'
})
export class UserHistoryService extends AuthenticatedDataService<UserHistoryModel[] | undefined> {
  userId?: string;

  constructor(
    auth: Auth,
    userIdService: UserIdService,
    userRolesService: UserRolesService,
    private firestore: Firestore,
    private errorReporting: ErrorReportingService,
  ) {
    super(auth, userIdService, userRolesService);
    this.refresh();
    this.init();
  }

  override async init() {
    const changeSubscription = this.userIdService
      .get$()
      .pipe(take(1))
      .subscribe(userId => {
        this.userId = userId;
        this.getHistory();
      });

    this.addSubscription(changeSubscription);
  }

  public addToHistory(id: string, type: 'group' | 'user') {
    if (!this.userId) {
      return;
    }

    this.writeHistoryEntry(this.userId, id, type).catch((err: unknown) => {
      // Recording history is bookkeeping we do behind the user's back, so a
      // failure must never reach them. Left unhandled the rejection lands in
      // Angular's ErrorHandler, which in production is Sentry's — reporting it
      // as an unhandled crash and opening the feedback dialog over a page that
      // is working fine.
      if (getErrorCode(err) === 'permission-denied') {
        // The session lost access mid-flight: the account was disabled, or the
        // listen stream reconnected with a token the backend no longer honours.
        // Expected, and the listener below reports the same loss.
        console.warn('UserHistoryService: history write denied', { itemType: type });
        return;
      }

      this.errorReporting.captureError('user-history.add', err, { itemType: type });
    });
  }

  // Seam so specs can drive the write's outcome without a Firestore backend.
  protected writeHistoryEntry(userId: string, id: string, type: 'group' | 'user'): Promise<void> {
    const ref = doc(this.firestore, `users/${userId}/history/${type}_${id}`);

    return setDoc(ref, {
      accessedAt: new Date(),
      itemType: type,
      itemId: id,
    }, { merge: true });
  }

  private getHistory() {
    if (!this.userId) {
      this.set(undefined);
      return;
    }

    const ref = collection(this.firestore, `users/${this.userId}/history`);

    const refQuery = query(
      ref,
      orderBy('accessedAt', 'desc'),
      limit(10)
    );

    const subscription = collectionData(refQuery, { idField: 'id' }).pipe(
      traceUntilFirst('firestore')
    )
      .subscribe({
        next: res => {
          const history = (res as UserHistoryModel[]).filter(item => !!item.itemName);
          this.set(history);
        },
        error: err => {
          this.handleListenerError('UserHistoryService', err);
        },
      });

    this.addSubscription(subscription);
  }
}
